import { TCPAccount } from "../Abstract/TCPAccount";
import { MailIdentity } from "./MailIdentity";
import { Folder, SpecialFolder, type MailShareCombinedPermissions, type MailShareIndividualPermissions } from "./Folder";
import type { EMail } from "./EMail";
import type { SMTPAccount } from "./SMTP/SMTPAccount";
import { ContactEntry } from "../Abstract/Person";
import type { PersonUID } from "../Abstract/PersonUID";
import { FilterRuleAction } from "./FilterRules/FilterRuleAction";
import { OAuth2 } from "../Auth/OAuth2";
import type { AttachmentStorage } from "../Abstract/Attachment";
import type { Calendar } from "../Calendar/Calendar";
import type { SetupInfo } from "./AutoConfig/SetupInfo";
import { appGlobal } from "../app";
import { sanitize } from "../../../lib/util/sanitizeDatatypes";
import { AbstractFunction, assert } from "../util/util";
import { notifyChangedProperty } from "../util/Observable";
import { Collection, ArrayColl } from 'svelte-collections';
import { gt } from "../../l10n/l10n";

export class MailAccount extends TCPAccount {
  readonly protocol: string = "mail";
  /** SMTP server
   * Only set for IMAP and POP3, but null for JMAP, Exchange etc. */
  @notifyChangedProperty
  _outgoing: SMTPAccount = null;
  spamStrategy: DeleteStrategy = DeleteStrategy.MoveToTrash;
  protected _inbox: Folder;
  readonly canSendOutgoingInvitations: boolean = true;
  /** @see `calendar` */
  protected calendarID: string;
  /** Where we got the config from, during setup */
  source: ConfigSource = null;
  storage: MailAccountStorage;
  /** Ways to store email content in RFC822 format. First storage will be used for reading. */
  contentStorage = new ArrayColl<MailContentStorage>();

  @notifyChangedProperty
  emailAddress: string;
  @notifyChangedProperty
  readonly identities = new ArrayColl<MailIdentity>();
  @notifyChangedProperty
  readonly filterRuleActions = new ArrayColl<FilterRuleAction>();
  setup: SetupInfo;

  readonly rootFolders: Collection<Folder> = new ArrayColl<Folder>();

  async startup() {
    await super.startup();
    await this.listFolders();
    assert(this.inbox, gt`Inbox not found`);
    await this.inbox.getNewMessages();
  }

  async listFolders(): Promise<void> {
    throw new AbstractFunction();
  }

  getAllFolders(): ArrayColl<Folder> {
    let allFolders = new ArrayColl<Folder>();
    function iterateFolders(folders: Collection<Folder>) {
      allFolders.addAll(folders);
      for (let folder of folders) {
        iterateFolders(folder.subFolders);
      }
    }
    iterateFolders(this.rootFolders);
    return allFolders;
  }
  findFolder(findFunc: (folder: Folder) => boolean): Folder | null {
    return findSubFolderFromList(this.rootFolders, findFunc);
  }

  get inbox(): Folder | null {
    return this._inbox ??
      (this._inbox = this.getSpecialFolder(SpecialFolder.Inbox)) ??
      this.rootFolders.first;
  }

  get outgoing(): SMTPAccount {
    return this._outgoing;
  }
  set outgoing(smtp: SMTPAccount) {
    this._outgoing = smtp;
    if (smtp && smtp.mainAccount != this) {
      smtp.mainAccount = this;
    }
  }

  /** When accepting an incoming invitation, put the meeting in this calendar, by default.
   * The user can still change it in the dropdown. */
  get calendar(): Calendar | null {
    let cal = appGlobal.calendars.find(cal => cal.id == this.calendarID);
    if (!cal) {
      this.calendar = cal = this.calendarsAvailable.first;
    }
    return cal;
  }
  set calendar(cal: Calendar | null) {
    this.calendarID = cal?.id;
    this.save()
      .catch(this.errorCallback);
  }
  get calendarsAvailable(): Collection<Calendar> {
    let dependentCalendars = this.dependentAccounts().filterObservable(acc => !!(acc as Calendar).events) as ArrayColl<Calendar>;
    return dependentCalendars?.hasItems
      ? dependentCalendars
      : appGlobal.calendars.filterObservable(cal => cal.canAcceptAnyInvitation);
  }

  /**
   * Send the email purely on the protocol level,
   * and save it in the Sent folder. Nothing else.
   * All higher-level send preparations (signature, encryption etc.)
   * are done in `ComposeActions.send()`.
   */
  async send(email: EMail): Promise<void> {
    throw new AbstractFunction();
  }

  /** Create a folder on the top level, sibling of Inbox.
   * @see Folder.createSubFolder() */
  async createToplevelFolder(name: string): Promise<Folder> {
    let folder = this.newFolder();
    folder.name = name;
    folder.parent = null;
    this.rootFolders.add(folder);
    return folder;
  }

  newFolder(): Folder {
    return new Folder(this);
  }

  newEMailFrom(): EMail {
    let folder = this.getSpecialFolder(SpecialFolder.Sent);
    let email = folder.newEMail();
    email.compose.generateMessageID();
    email.loadedBody = true;
    email.from.emailAddress = this.emailAddress;
    email.from.name = this.realname;
    return email;
  }

  async save(): Promise<void> {
    await super.save();
    console.log("mail account save", this.protocol, this.name, this, "outgoing", this.outgoing, "mainaccount", this.mainAccount, "storage", this.storage);
    await this.storage?.saveAccount(this);
  }

  async deleteIt(): Promise<void> {
    await super.deleteIt();
    await this.storage?.deleteAccount(this);
    appGlobal.emailAccounts.remove(this);
  }

  isMyEMailAddress(emailAddress: string): boolean {
    return this.emailAddress == emailAddress ||
      this.identities.some(id => id.isEMailAddress(emailAddress));
  }

  /**
   * @returns `MailIdentity` of *this* account, for the given email address.
   * @see also global function `findIdentityForEMailAddress()`, which searches all accounts. */
  findIdentityForEMailAddress(emailAddress: string): MailIdentity | null {
    for (let identity of this.identities) {
      if (identity.isEMailAddress(emailAddress)) {
        return identity;
      }
    }
    return null;
  }

  /** Get the `specialFolder` in this account. */
  getSpecialFolder(specialFolder: SpecialFolder): Folder {
    let folder = this.getAllFolders().find(folder => folder.specialFolder == specialFolder);
    if (folder) {
      return folder;
    }
    if (specialFolder == SpecialFolder.Sent) {
      return this.getSpecialFolder(SpecialFolder.Inbox);
    }
    if (specialFolder == SpecialFolder.Drafts) {
      return this.getSpecialFolder(SpecialFolder.Sent);
    }
    return this.rootFolders.first;
  }

  canShareWithPersons(): boolean {
    return false;
  }

  async getSharedPersons(): Promise<ArrayColl<PersonUID>> {
    return new ArrayColl<PersonUID>();
  }

  async deleteSharedPerson(Person: PersonUID) {
  }

  async addSharedPerson(person: PersonUID, mailFolder: Folder | null, includeSubfolders: boolean, access: MailShareCombinedPermissions, ...permissions: MailShareIndividualPermissions[]) {
  }

  fromConfigJSON(json: any) {
    super.fromConfigJSON(json);
    this.emailAddress = sanitize.emailAddress(json.emailAddress);
    this.identities.clear();
    for (let idJSON of sanitize.array(json.identities, [])) {
      try {
        this.identities.add(MailIdentity.fromConfigJSON(idJSON, this));
      } catch (ex) {
        this.errorCallback(ex);
      }
    }
    this.filterRuleActions.clear();
    for (let filterJSON of sanitize.array(json.filterRuleActions, [])) {
      try {
        let rule = new FilterRuleAction(this);
        rule.fromJSON(filterJSON);
        this.filterRuleActions.add(rule);
      } catch (ex) {
        this.errorCallback(ex);
      }
    }
    if (json.oAuth2) {
      this.oAuth2 = OAuth2.fromConfigJSON(json.oAuth2, this);
      this.oAuth2.subscribe(() => this.notifyObservers());
    }
    // On startup, the calendar might not be read yet, so we store the ID and resolve in the getter.
    this.calendarID = sanitize.alphanumdash(json.calendarID, null);

    if (!appGlobal.me.name && this.realname) {
      appGlobal.me.name = this.realname;
    }
    if (!appGlobal.me.emailAddresses.find(c => c.value == this.emailAddress)) {
      appGlobal.me.emailAddresses.add(new ContactEntry(this.emailAddress, "account"));
    }
  }
  toConfigJSON(): any {
    let json = super.toConfigJSON();
    json.identities = this.identities.contents.map(id => id.toConfigJSON());
    json.filterRuleActions = this.filterRuleActions.contents.map(rule => rule.toJSON());
    json.oAuth2 = this.oAuth2 ? this.oAuth2.toConfigJSON() : undefined;
    json.emailAddress = this.emailAddress;
    json.calendarID = this.calendarID;
    return json;
  }

  /** Fills this object with the config values from the other config */
  cloneFrom(other: MailAccount) {
    this.url = other.url;
    this.hostname = other.hostname;
    this.port = other.port;
    this.tls = other.tls;
    this.authMethod = other.authMethod;
    this.username = other.username;
    this.password = other.password;
    this.emailAddress = other.emailAddress;
    this.realname = other.realname;
    this.calendarID = other.calendarID;

    // objects
    this.oAuth2 = other.oAuth2;
    this.identities.addAll(other.identities);
  }

  toDebugString() {
    return `${this.protocol.toUpperCase()} account, name ${this.name}, ID ${this.id}, host ${this.hostname}:${this.port}, URL ${this.url}, username ${this.username}, email ${this.emailAddress}`;
  }

  toString(): string {
    return this.toDebugString();
  }
}

function findSubFolderFromList(folders: Collection<Folder>, findFunc: (folder: Folder) => boolean): Folder | null {
  for (let folder of folders) {
    if (findFunc(folder)) {
      return folder;
    }
    let sub = findSubFolderFromList(folder.subFolders, findFunc);
    if (sub) {
      return sub;
    }
  }
  return null;
}

export type ConfigSource = "ispdb" | "autoconfig-isp" | "autodiscover-xml" | "autodiscover-json" | "guess" | "manual" | "harddisk" | "builtin" | null;

export interface MailAccountStorage {
  saveAccount(account: MailAccount): Promise<void>;
  deleteAccount(account: MailAccount): Promise<void>;
  readFolderHierarchy(account: MailAccount): Promise<void>;
  saveFolder(folder: Folder): Promise<void>;
  saveFolderProperties(folder: Folder): Promise<void>;
  deleteFolder(folder: Folder): Promise<void>;
  readMessage(email: EMail): Promise<void>;
  readMessageWritableProps(email: EMail): Promise<void>;
  readMessageBody(email: EMail): Promise<void>;
  saveMessage(email: EMail): Promise<void>;
  saveMessages(emails: Collection<EMail>): Promise<void>;
  saveMessageWritableProps(email: EMail): Promise<void>;
  saveMessageTags(email: EMail): Promise<void>;
  deleteMessage(email: EMail): Promise<void>;
  readAllMessages(folder: Folder, limit?: number, startWith?: number): Promise<void>;
  readAllMessagesMainProperties(folder: Folder, limit?: number, startWith?: number): Promise<void>;
}

export interface MailContentStorage extends AttachmentStorage {
  save(email: EMail): Promise<void>;
  read(email: EMail): Promise<void>;
  deleteIt(email: EMail): Promise<void>;
}

export enum DeleteStrategy {
  DeleteImmediately = 1,
  Flag = 2,
  MoveToTrash = 3,
}
